import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { base64StringToBlob } from 'blob-util';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DocumentExportAPIService } from 'src/app/extensions/services/api/documentExport-api.service';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { PaymentAPIService } from 'src/app/extensions/services/api/payment-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { PaymentListFilterComponent } from './payment-list-filter.component.ts/payment-list-filter.component';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subscription } from 'rxjs';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class PaymentListComponent implements OnInit, OnDestroy {
  public payments: app.models.GetPagedPaymentsModel[];
  public props: app.models.DynamicPropsDictionary;
  public subscriptions: Subscription[] = [];
  private pageIndex = 1;
  private maxIndex: number;
  public activeFilterCount = 0;
  public loading: boolean = false;
  public showBackToTop: boolean = false;
  private pageFilter: app.models.GetPagedPaymentsPageFilterModel;
  initialFilterState: app.models.GetPagedPaymentsPageFilterModel;

  client: app.models.Partner;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  public hasData = true;
  public hasFilterData = true;
  public windowSizeLgUp: boolean;

  public constructor(
    private _globalService: GlobalService,
    private _scrollService: ScrollService,
    private _paymentAPIService: PaymentAPIService,
    private _ngbModal: NgbModal,
    private _router: Router,
    private _partnerAPIService: PartnerAPIService,
    private _documentExportAPIService: DocumentExportAPIService,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _windowEventService: WindowEventsService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    this._partnerAPIService.getClient().subscribe(result => {
      this.client = result;
    });
    this.pageFilter = {
      startDate: null,
      endDate: null,
    };
    this.initialFilterState = structuredClone(this.pageFilter);
    this.loadData();

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);
    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._paymentAPIService.getPagedPayments(this.pageIndex, this.pageFilter).subscribe(x => {
          this.payments = this.payments.concat(x.data.values);
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let resizeSubscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(resizeSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData() {
    this._paymentAPIService.getPagedPayments(this.pageIndex, this.pageFilter).subscribe(x => {
      this.payments = x.data.values;
      this.hasData = this.payments.length != 0;
      this.props = x.props;
      this.maxIndex = x.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  public filterHandler() {
    let instance = this._ngbModal.open(PaymentListFilterComponent);
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = structuredClone(this.pageFilter);
        this.pageIndex = 1;

        this.activeFilterCount = result;

        this._paymentAPIService.getPagedPayments(this.pageIndex, this.pageFilter).subscribe(x => {
          this.payments = x.data.values;
          this.hasFilterData = this.payments.length != 0;
          this.props = x.props;
          this.maxIndex = x.data.totalPages;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });
      }
    });
  }

  public resetFiltersHandler() {
    this.pageFilter.startDate = null;
    this.pageFilter.endDate = null;
    this.activeFilterCount = 0;
    this.hasFilterData = true;
    this.pageIndex = 1;
    this.loadData();
  }

  public navigateToDetailHandler = (paymentId: string) => {
    this._router.navigate(['payments', paymentId]);
  };

  public downloadPayments() {
    this.loading = true;
    this._documentExportAPIService.exportPayments(this.client, this.pageFilter).subscribe(
      file => {
        let blob = base64StringToBlob(file.response.buffer, file.response.contentType);
        let link = document.createElement('a');
        let objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;

        let dateTime = new Date();
        let date = dateTime.getFullYear() + '-' + (dateTime.getMonth() + 1) + '-' + dateTime.getDate();
        let time = dateTime.getHours() + '-' + dateTime.getMinutes() + '-' + dateTime.getSeconds();

        let generatedFileName = `i_${date}_${time}`;
        link.download = `${generatedFileName}.${file.response.contentType}`;
        link.click();
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  showClipboardNotification() {
    this._notificationService.showSuccess(
      this._translateService.instant('common.copiedToClipboard'),
      this._translateService.instant('common.success')
    );
  }

  scrollToTop(): void {
    scrollTop();
  }
}
